import * as Phaser from "phaser"
import { GAME_WIDTH } from "../../data/constants"
import type { EnemyData, GameSprite } from "../../types"
import {
  type EnemyState,
  type EnemyStateContext,
  StatefulEnemyRuntime,
} from "../enemy-system"

const DIVE_TRIGGER_DISTANCE = 120
const DIVE_SPEED = 14
const DIVE_COOLDOWN_MS = 2000
const HUNT_ENTRY_TIME_MS = 200
const MIN_DIVE_DISTANCE = 50

type KamikazePhase = "hunting" | "diving"

export class KamikazeEnemy extends StatefulEnemyRuntime<KamikazePhase> {
  private diveCooldown: number
  private diveDirectionX = 0
  private diveDirectionY = 0

  public constructor(sprite: GameSprite, enemyData: EnemyData) {
    const states: EnemyState<KamikazePhase>[] = [
      {
        name: "hunting",
        behaviorState: "flying",
        animationState: "flying",
        update: (context) => this.updateHunting(context),
      },
      {
        name: "diving",
        behaviorState: "diving",
        animationState: "diving",
        update: (context) => this.updateDiving(context),
      },
    ]

    super(sprite, enemyData, "hunting", states)
    this.diveCooldown = DIVE_COOLDOWN_MS + Math.random() * 1000
  }

  private updateHunting(context: EnemyStateContext): KamikazePhase | null {
    const { sprite, enemyData, stateTime, delta, playerX } = context

    this.diveCooldown -= delta

    if (enemyData.convoyStopX !== undefined) {
      this.applyConvoyMovement(context)
    } else {
      this.applyBaseMovement(context)
    }

    // Fixed: distance check was inverted (playerX - sprite.x is negative if enemy is on right)
    const distanceToPlayerX = sprite.x - playerX
    const isInDiveRange =
      (distanceToPlayerX > 0 && distanceToPlayerX < DIVE_TRIGGER_DISTANCE) ||
      (this.diveCooldown <= 0 && sprite.x < GAME_WIDTH * 0.65)

    if (isInDiveRange && stateTime > HUNT_ENTRY_TIME_MS) {
      this.initiateDive(context)
      this.diveCooldown = DIVE_COOLDOWN_MS + Math.random() * 1000
      return "diving"
    }

    if (sprite.x < 30) {
      enemyData.vx = 3
      enemyData.vy = 0
    }

    return null
  }

  private updateDiving(context: EnemyStateContext): KamikazePhase | null {
    const { enemyData, scrollCompensation } = context

    enemyData.vx = this.diveDirectionX * DIVE_SPEED + scrollCompensation
    enemyData.vy = this.diveDirectionY * DIVE_SPEED

    return null
  }

  private initiateDive(context: EnemyStateContext): void {
    const { sprite, playerX, playerY } = context

    const dx = playerX - sprite.x
    const dy = playerY - sprite.y
    const dist = Math.sqrt(dx * dx + dy * dy)

    if (dist > MIN_DIVE_DISTANCE) {
      this.diveDirectionX = dx / dist
      this.diveDirectionY = dy / dist
    } else {
      this.diveDirectionX = -1
      this.diveDirectionY = 0
    }
  }

  protected override applyBaseMovement(context: EnemyStateContext): void {
    const { sprite, enemyData, playerY, delta, scrollCompensation } = context

    // Move toward player Y level
    const targetY = playerY
    const easeY = 1 - (1 - 0.08) ** (delta / 16.6)
    sprite.y = Phaser.Math.Linear(sprite.y, targetY, easeY)

    // Base horizontal movement
    enemyData.vx = -4.5 + scrollCompensation
    enemyData.vy = 0
  }

  private applyConvoyMovement(context: EnemyStateContext): void {
    const { sprite, enemyData, time, delta, scrollCompensation } = context

    const convoyIndex = enemyData.convoyIndex ?? 0
    const stopX = enemyData.convoyStopX ?? enemyData.homeX ?? sprite.x
    const homeY = enemyData.homeY ?? sprite.y
    const baseOffset = convoyIndex * 150
    const wave = Math.sin((time + baseOffset) / 600) * 20
    const drift = Math.cos((time + baseOffset * 0.7) / 400) * 12

    const targetY = homeY + wave + drift
    const targetX = stopX - 40 + Math.sin((time + baseOffset) / 800) * 30

    const ease = 1 - (1 - 0.16) ** (delta / 16.6)
    sprite.y = Phaser.Math.Linear(sprite.y, targetY, ease)

    const vxDelta = (targetX - sprite.x) * 0.05 + scrollCompensation + 1.2
    enemyData.vx = Phaser.Math.Linear(enemyData.vx, vxDelta, ease * 0.7)
    enemyData.vy = 0
  }
}
